/**
 * Data model for quantity rule
 * @author Alexis Tugilimana <alexis.tugilimana@kabandy.com>
 * @copyright (c) Kabandy 2016-2022. All rights reserved.
 */
import { ITime, ITimeSerialized } from ".";
import { AutoselectIds } from "../Autoselect/AutoselectList";
import { QuantityDataModel } from "./Quantity";

export module QuantityRuleDataModel {
    export interface IAbstractQuantityRule {
        name: string;
        multiplier: number;
        viewMode: QuantityDataModel.EQuantityMode;
        unit: QuantityDataModel.EQuantityUnits;
        selectionType: QuantityDataModel.EQuantityUnitType;
        visibility: IRuleVisibilityCriterion[];
        selection: IRuleSelectionCriterion[];
        autoselectType?: QuantityDataModel.EAutoselectType;
    }

    export interface IAbstractQuantityRuleGetInfo {
        id: string;
        projectId: string;
    }

    export interface IAbstractQuantityRuleDeleteInfo {
        id: string;
        projectId: string;
    }

    export interface IAbstractQuantityRuleCreateInfo {
        id: string;
        projectId: string;
    }

    export interface IAbstractQuantityRuleCopyInfo {
        userId?: string;
        projectIdSrc: string;
        projectIdDest: string;
        idSrc: string;
        idDest?: string;
    }

    export interface IQuantityRuleFile {
        visibility: IRuleVisibilityCriterion[];
        selection: IRuleSelectionCriterion[];
    }

    export enum EQuantityRuleFileName {
        QuantityRule = "quantity-rule.json"
    }

    export interface IStorateFileCreateInfo {
        filename: EQuantityRuleFileName;
        content?: any;
        contentFilePath?: string;
        contentType: string;
        metadata?: any;
        compressed: boolean;
    }

    //
    // --- Visibility
    export interface IRuleVisibilityCriterion {
        id?: string;
        propertyName: string;
        rules: IRule[];
    }

    //
    // --- Selection
    export interface IRuleSelectionCriterion {
        autoselectId: AutoselectIds;
        criteria: IRuleVisibilityCriterion[]
    }

    //
    // --- Rule parameters
    export enum ERuleType {
        Enumeration = "enumeration",
        Regex = "regex"
    }

    
    export interface IRule {
        type: ERuleType;
        ruleParameters: IRuleEnumerationParameters | IRuleRegexParameters;
    }

    // Enumeration
    export interface IRuleEnumerationParameters {
        values: string[];
    }

    // Regex
    export enum ERegexOperator {
        Equal= "equal",
        NotEqual = "not equal"
    }

    export interface IRuleRegexParameters {
        pattern: string;
        flag?: string;
        operator: ERegexOperator
    }

    //
    // --- Database
    export module Database {
        export interface IQuantityRule {
            id: string;
            projectId: string;
            name: string;
            multiplier: number;
            viewMode: QuantityDataModel.EQuantityMode;
            unit: QuantityDataModel.EQuantityUnits;
            selectionType: QuantityDataModel.EQuantityUnitType;
            time: ITime | ITimeSerialized;
        }

        export interface IQuantityRuleCreateInfo extends IAbstractQuantityRuleCreateInfo {
            name: string;
            multiplier: number;
            viewMode: QuantityDataModel.EQuantityMode;
            unit: QuantityDataModel.EQuantityUnits;
            selectionType: QuantityDataModel.EQuantityUnitType;
        }

        export interface IQuantityRuleGetInfo extends IAbstractQuantityRuleGetInfo {}

        export interface IQuantityRuleDeleteInfo extends IAbstractQuantityRuleDeleteInfo {}
    }

    //
    // --- Storage
    export module Storage {
        export interface IQuantityRule {
            id: string;
            projectId: string;
            quantityRule: IQuantityRuleFile;
        }

        export interface IQuantityRuleCreateInfo extends IAbstractQuantityRuleCreateInfo {
            fileCreateInfos: IStorateFileCreateInfo[];
        }

        export interface IQuantityRuleGetInfo extends IAbstractQuantityRuleGetInfo {}

        export interface IQuantityRuleDeleteInfo extends IAbstractQuantityRuleDeleteInfo {}

        export interface IQuantityRuleCopyInfo extends IAbstractQuantityRuleCopyInfo{}
    }

    //
    // --- Runtime
    export module Runtime {
        export interface IQuantityRule extends IAbstractQuantityRule {
            id: string;
            projectId: string;
        }

        export interface IQuantityRuleCreateInfo {
            id?: string;
            projectId: string;
            database: {
                name: string;
                multiplier: number;
                viewMode: QuantityDataModel.EQuantityMode;
                unit: QuantityDataModel.EQuantityUnits;
                selectionType: QuantityDataModel.EQuantityUnitType;
            },
            storage: {
                fileCreateInfos: IStorateFileCreateInfo[];
            }
        }

        export interface IQuantityRuleGetInfo extends IAbstractQuantityRuleGetInfo {}

        export interface IQuantityRuleDeleteInfo extends IAbstractQuantityRuleDeleteInfo {}

        export interface IQuantityRuleCopyInfo extends IAbstractQuantityRuleCopyInfo{}
    }

    export module Web {
        export interface IQuantityRuleTmp {
            id?: string;
            name: string;
            multiplier: number;
            viewMode: QuantityDataModel.EQuantityMode;
            unit: QuantityDataModel.EQuantityUnits;
            selectionType: QuantityDataModel.EQuantityUnitType;
            quantityRuleTmpUuid?: string;
        }

        export interface IQuantityRule {
            name: string;
            multiplier: number;
            viewMode: QuantityDataModel.EQuantityMode;
            unit: QuantityDataModel.EQuantityUnits;
            selectionType: QuantityDataModel.EQuantityUnitType;
            quantityRuleFilePath?: string;
        }

        export interface IQuantityRuleGetInfo extends IAbstractQuantityRuleGetInfo {}

        export interface IQuantityRuleDeleteInfo extends IAbstractQuantityRuleDeleteInfo {}
    }
}
